/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from 'react'
import { LineArrowIcon, SidebarIcon, SignOutIcon, SupportIcon } from '@/src/assets/icons'
import { Box, Drawer, List, Menu, MenuItem, Typography } from '@mui/material'
import { useRouter } from 'next/router'
import { Avatar } from '../AvatarIcon'
import { useStore } from '@/src/types/store'
import { useTier } from '@/src/hooks/useTier'
import { NewSideBarMenu, UserSidebarData } from '../sidebar/data'
import { Button } from '@mui/base'
import Link from 'next/link'
import Notification from '../Notification'
import { formatPrice } from '@/src/types/helper'
import CommonApi from '@/pages/api'
import { CurrentStats } from '@/pages/api/endpoints/MarketData'

export interface Tab {
  id: string | number
  read?: boolean | undefined
  write?: boolean | undefined
  name: string
  title: string
  href: string
}

// Note**: Arbitrary values because tailwind values were not matching figma
const Header = ({ toggleSidebar }: any) => {
  const [showDrawer, setShowDrawer] = useState(false)
  const [isRotated, setIsRotated] = useState(false)
  const {
    profile,
    clearProfile,
    themeLogo,
    statsData,
    setStatsData,
    permissions,
    homeTheme,
    openToast
  } = useStore()
  const router = useRouter()

  useEffect(() => {
    if (profile?.user_type !== 'investor') return
    if (statsData?.current_price) return
    const fetchCurrentStats = async () => {
      try {
        const resp = await CommonApi({
          path: CurrentStats?.get({ interval: 'month' }),
          method: 'get'
        })
        if (resp?.status) {
          setStatsData(resp.data)
        }
      } catch (err) {
        console.log('err', err)
      }
    }
    fetchCurrentStats()
  }, [profile?.user_type, statsData?.current_price, setStatsData])
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  // Essentials/Intelligence tenants have no investor centre; their public
  // surface is the branded landing page at the root, so home and sign-out
  // both resolve there regardless of any custom-site settings. Surface-derived,
  // never plan-derived — see useTier.
  const { isLandingTenant } = useTier()
  // Custom-site tenants land on their Investor Centre page; the marketing
  // homepage (custom_site_url) is only the fallback while the newer
  // Investor centre page URL is unset.
  const externalHome =
    !isLandingTenant && homeTheme?.is_custom_site
      ? homeTheme?.investor_centre_url || homeTheme?.custom_site_url || null
      : null
  const handleLogout = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const LogoutUser = async () => {
    setAnchorEl(null)

    try {
      const response = await CommonApi({
        method: 'post',
        path: `signout`
      })

      if (response?.status) {
        openToast(response?.msg, 'success')
      }
    } catch (error) {
      console.log(error)
    } finally {
      clearProfile()

      if (isLandingTenant) {
        // The landing page is the sign-in surface for these plans.
        router.push('/')
      } else if (externalHome) {
        window.location.href = externalHome
      } else {
        router.push({
          pathname: '/login',
          query: {
            user_type: 'user'
          }
        })
      }
    }
  }

  const handleprofile = () => {
    router.push('/my-profile')
    handleClose()
  }

  const toggleDrawer = () => {
    setShowDrawer(true)
  }

  const handlePermission = (tab: Tab) => {
    const findTab = permissions?.find(item => item?.name === tab?.name)
    return findTab?.read === true
  }

  const CurrTab = router?.pathname
  const isUser = profile?.user_type === 'investor' || profile?.user_type === 'user'
  const drawerMenu = isUser ? UserSidebarData(CurrTab) : NewSideBarMenu(CurrTab)

  const handleToggle = () => {
    setIsRotated(prev => !prev) // rotate the icon
    toggleSidebar() // call parent's sidebar logic
  }

  const SignOut = async () => {
    try {
      const response = await CommonApi({
        method: 'post',
        path: `signout`
      })
      if (response?.status) {
        openToast(response?.msg, 'success')
      }
    } catch (error) {
      console.log(error, error)
    } finally {
    }
  }

  return (
    <div className="fixed flex z-30 justify-between border-b-2 w-full divide-red-300 px-4 !h-[64px] text-l shadow-headerShadow relative bg-primary-main">
      <div className="flex  items-center">
        <div className="flex gap-2 items-center">
          <div className="block sm:hidden" onClick={toggleDrawer}>
            <SidebarIcon className="w-4 h-3.5 fill-[#ffffff]" />
          </div>
          <div
            className={`hidden sm:block cursor-pointer transition-transform duration-300 ease-in-out 
          ${isRotated ? 'rotate-180' : 'rotate-0'}`}
            data-modal="true"
          >
            <svg
              onClick={handleToggle}
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.95624 0.729981H15.0438C16.4068 0.729973 17.4908 0.729966 18.3654 0.801423C19.261 0.874596 20.0247 1.02766 20.7239 1.38394C21.8529 1.95918 22.7708 2.87707 23.346 4.00604C23.7023 4.70529 23.8554 5.46896 23.9286 6.36456C24 7.23914 24 8.32316 24 9.6862V15.7738C24 17.1368 24 18.2208 23.9286 19.0954C23.8554 19.991 23.7023 20.7547 23.346 21.4539C22.7708 22.5829 21.8529 23.5008 20.7239 24.076C20.0247 24.4323 19.261 24.5854 18.3654 24.6585C17.4908 24.73 16.4068 24.73 15.0438 24.73H8.95622C7.59318 24.73 6.50916 24.73 5.63458 24.6585C4.73898 24.5854 3.9753 24.4323 3.27606 24.076C2.14709 23.5008 1.2292 22.5829 0.653961 21.4539C0.297677 20.7547 0.144616 19.991 0.0714424 19.0954C-1.43722e-05 18.2208 -7.93413e-06 17.1368 1.72105e-07 15.7737V9.68622C-7.93413e-06 8.32318 -1.43722e-05 7.23915 0.0714424 6.36456C0.144616 5.46896 0.297677 4.70529 0.653961 4.00604C1.2292 2.87707 2.14708 1.95918 3.27606 1.38394C3.9753 1.02766 4.73898 0.874596 5.63458 0.801423C6.50917 0.729966 7.5932 0.729973 8.95624 0.729981ZM5.79744 2.79478C5.02552 2.85785 4.55435 2.97727 4.18404 3.16595C3.43139 3.54945 2.81947 4.16137 2.43597 4.91402C2.24729 5.28433 2.12787 5.7555 2.0648 6.52742C2.00078 7.31102 2 8.31335 2 9.72998V15.73C2 17.1466 2.00078 18.1489 2.0648 18.9325C2.12787 19.7045 2.24729 20.1756 2.43597 20.5459C2.81947 21.2986 3.43139 21.9105 4.18404 22.294C4.55435 22.4827 5.02552 22.6021 5.79744 22.6652C6.38254 22.713 7.08959 22.7255 8 22.7288V2.73115C7.08959 2.73443 6.38254 2.74698 5.79744 2.79478ZM10 2.72998V22.73H15C16.4166 22.73 17.419 22.7292 18.2026 22.6652C18.9745 22.6021 19.4457 22.4827 19.816 22.294C20.5686 21.9105 21.1805 21.2986 21.564 20.5459C21.7527 20.1756 21.8721 19.7045 21.9352 18.9325C21.9992 18.1489 22 17.1466 22 15.73V9.72998C22 8.31335 21.9992 7.31102 21.9352 6.52742C21.8721 5.7555 21.7527 5.28433 21.564 4.91402C21.1805 4.16137 20.5686 3.54945 19.816 3.16595C19.4457 2.97727 18.9745 2.85785 18.2026 2.79478C17.419 2.73076 16.4166 2.72998 15 2.72998H10Z"
                fill="white"
              />
              <path
                d="M17.0815 17.6925C17.472 18.083 18.1052 18.083 18.4957 17.6925C18.8862 17.302 18.8862 16.6688 18.4957 16.2783L15.2028 12.9854L18.4957 9.69252C18.8862 9.302 18.8862 8.66883 18.4957 8.27831C18.1052 7.88778 17.472 7.88778 17.0815 8.27831L13.7886 11.5712C13.0075 12.3522 13.0075 13.6186 13.7886 14.3996L17.0815 17.6925Z"
                fill="white"
              />
            </svg>
          </div>
          <Link href={externalHome ?? '/'} className="hidden md:block">
            <img
              src={themeLogo?.dark_logo ?? themeLogo?.logo}
              alt="Logo"
              className="h-10 w-auto max-w-[12rem] object-contain fill-none ml-4"
            />
          </Link>
        </div>
      </div>
      <div className="flex flex-col justify-start"></div>
      <div className="flex  justify-right items-center gap-2">
        {profile?.user_type === 'investor' && (
          <>
            <Typography variant="subtitle2" className="text-white">
              ASX:{homeTheme?.code}
            </Typography>
            {statsData?.current_price ? (
              <>
                <Typography variant="subtitle2" className="text-white">
                  $
                </Typography>
                <Typography variant="subtitle2" className="text-white">
                  {formatPrice(statsData?.current_price) || '0.00'}
                </Typography>
              </>
            ) : null}
            {statsData?.percentage != null && (
              <Box
                className={`w-full ${
                  statsData?.percentage === 0
                    ? 'bg-secondary-lighterGray'
                    : statsData?.percentage > 0
                      ? 'bg-[#EDF7ED]'
                      : 'bg-white'
                } flex px-2 py-[2px] gap-1 rounded-3xl items-center justify-center`}
              >
                {statsData?.percentage !== 0 && (
                  <LineArrowIcon
                    className={`w-[8px] h-[8px] ${
                      statsData?.percentage > 0
                        ? 'fill-[#2E7D32] -rotate-90'
                        : 'fill-[#D32F2F] rotate-90'
                    } `}
                  />
                )}
                <Typography
                  variant="caption"
                  className="truncate"
                  color={
                    statsData?.percentage === 0
                      ? ''
                      : statsData?.percentage > 0
                        ? 'success.dark'
                        : 'error.dark'
                  }
                >
                  {statsData?.percentage?.toFixed(2) || '00'}%
                </Typography>
              </Box>
            )}
          </>
        )}
        {profile?.user_type == '' ? (
          <Button
            className="bg-primary-main p-2 text-s rounded-xl text-white font-semibold"
            onClick={() => {
              clearProfile()
              router.push('/login')
            }}
          >
            Login
          </Button>
        ) : (
          <>
            <Notification />
            <button data-testid="automation-IRHQ_Header-Avatar" onClick={handleLogout}>
              <Avatar profile={profile} />
            </button>
          </>
        )}

        <Menu
          className="mt-6 w-full"
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button'
          }}
          classes={{
            paper: 'rounded-[10px] min-w-[220px]'
          }}
        >
          <div className="p-0 py-2 gap-2 flex flex-col">
            <div className="flex flex-col gap-1 justify-start px-4 py-2">
              <div className="flex gap-2">
                <div className="flex items-top">
                  <Avatar
                    profile={profile}
                    data-testid="automation-IRHQ_HeaderMenu-Avatar"
                    className="w-10 h-10 bg-primary-main text-white rounded-full flex justify-center items-center"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Typography variant="subtitle1" color="text.primary">
                    {' '}
                    {profile?.first_name || 'user'} {profile?.last_name || ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {' '}
                    {profile?.email || 'josn@mail.com'}
                  </Typography>
                  <div
                    className="cursor-pointer text-primary-main"
                    onClick={handleprofile}
                    data-testid="automation-IRHQ_HeaderMenu-ManageProfile"
                  >
                    <Typography variant="buttonLink">{'Manage Profile'}</Typography>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <hr />
            </div>
            <div className="flex flex-col ">
              <Link
                href={`mailto:${profile?.service_email ? profile?.service_email : 'support@relait.com.au'}`}
                target="_blank"
              >
                <MenuItem
                  data-testid="automation-IRHQ_HeaderMenu-Profile"
                  className="flex gap-2 py-[6px] px-4"
                >
                  <div>
                    <SupportIcon className="w-6 h-6 fill-none" />
                  </div>
                  <Typography variant="menuItemDefault">Support</Typography>
                </MenuItem>
              </Link>
              <MenuItem
                onClick={LogoutUser}
                data-testid="automation-IRHQ_HeaderMenu-Signout"
                className="flex gap-2 py-[6px] px-4"
              >
                <div>
                  <SignOutIcon className="w-6 h-6 fill-none" />
                </div>
                <div>
                  <Typography variant="menuItemDefault">Sign out</Typography>
                </div>
              </MenuItem>
            </div>
          </div>
        </Menu>
        <Drawer
          open={showDrawer}
          className="flex flex-col gap-4"
          onClose={() => setShowDrawer(false)}
        >
          <div className=" bg-primary-main w-[250px] h-full min-h-screen ">
            <div className="flex justify-center my-6">
              <Link href={externalHome ?? '/'}>
                <img
                  src={themeLogo.dark_logo}
                  alt="Logo"
                  className="h-10 w-auto max-w-[12rem] object-contain fill-none"
                />
              </Link>
            </div>
            <List className="flex flex-col gap-4">
              {drawerMenu.map(
                (tab: any, index) =>
                  (isUser ? true : handlePermission(tab)) && (
                    <div
                      key={index}
                      className={`flex items-center text-white cursor-pointer gap-3.5 w-full py-2 px-4 ${
                        CurrTab === tab?.href
                          ? ' bg-secondary-blue text-white'
                          : 'hover:bg-secondary-blue'
                      }`}
                      onClick={() =>
                        router.push({
                          pathname: `${tab?.href}`
                        })
                      }
                    >
                      <div
                        style={{
                          fontWeight: CurrTab === tab?.href ? 'bold ' : 'normal'
                        }}
                      >
                        {tab?.icon}
                      </div>
                      <span className={`w-44 `}>{tab?.title}</span>
                    </div>
                  )
              )}
            </List>
          </div>
        </Drawer>
      </div>
    </div>
  )
}

export default Header
